import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, KpiCard } from "@cx/ui";
import type { CohortResult, CustomersResult, PortfolioRowView } from "@cx/contracts";
import { formatDateBR, todayInSaoPaulo, type PortfolioBucket } from "@cx/domain";
import { useSession } from "../../session";
import { ChartCard } from "../dashboard/ChartCard";
import { count, money, monthYear, pct, periodLabel, reportQuery } from "../dashboard/labels";
import { FilterRow, ReportStatus, StatusFilter, reportQueryOptions } from "../dashboard/shared";
import { DateField, ExportButtons, HistoryStatus, MultiFilter, listOf, useAnalyticsStatus } from "./shared";

const PATH = "/meu-negocio/clientes";
const BUCKETS: [PortfolioBucket, string][] = [
  ["active", "Ativos"],
  ["risk", "Em risco"],
  ["inactive", "Inativos"],
];

/**
 * Clientes (RF-BI-11): novos × recorrentes por mês, atividade da carteira por recência (ativos, em risco, inativos) e
 * cohort pelo mês da primeira compra — tudo do histórico no lago.
 */
export function CustomersPage(): ReactElement {
  const { companyId, api } = useSession();
  const search = useSearch({ from: PATH });
  const navigate = useNavigate({ from: PATH });
  const status = useAnalyticsStatus();
  const set = (patch: Partial<typeof search>) =>
    navigate({ search: (s) => ({ ...s, ...patch }), replace: true });
  const [from, to] = search.from && search.to ? [search.from, search.to] : [undefined, undefined];
  const params = {
    from,
    to,
    channels: search.channels,
    statuses: search.statuses,
    activeDays: search.activeDays?.toString(),
    riskDays: search.riskDays?.toString(),
  };
  const q = useQuery({
    ...reportQueryOptions,
    queryKey: ["analytics", companyId, "customers", params],
    queryFn: () => api<CustomersResult>(`/analytics/customers${reportQuery(params)}`),
  });
  const r = q.data;
  return (
    <>
      <ContentHeader
        title="Clientes"
        breadcrumb={["Meu Negócio", "Clientes"]}
        actions={
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <HistoryStatus />
            {r?.lists && <ExportButtons report="customers" params={params} />}
          </div>
        }
      />
      <FilterRow>
        <DateField id="cli-from" label="De" value={search.from} onChange={(v) => set({ from: v })} />
        <DateField id="cli-to" label="Até" value={search.to} onChange={(v) => set({ to: v })} />
        <div className="col-6 col-md-2">
          <label className="form-label small" htmlFor="cli-active">
            Ativo até (dias)
          </label>
          <input
            id="cli-active"
            type="number"
            min={7}
            max={365}
            className="form-control"
            value={search.activeDays ?? 90}
            onChange={(e) => set({ activeDays: Number(e.target.value) || undefined })}
          />
        </div>
        <div className="col-6 col-md-2">
          <label className="form-label small" htmlFor="cli-risk">
            Em risco até (dias)
          </label>
          <input
            id="cli-risk"
            type="number"
            min={14}
            max={730}
            className="form-control"
            value={search.riskDays ?? 180}
            onChange={(e) => set({ riskDays: Number(e.target.value) || undefined })}
          />
        </div>
        <div className="col-6 col-md-2">
          {status.data && (
            <MultiFilter
              label="Canais"
              allLabel="Todos os canais"
              options={status.data.channels}
              value={listOf(search.channels)}
              onChange={(v) => set({ channels: v?.join(",") })}
            />
          )}
        </div>
        <div className="col-6 col-md-2">
          {status.data && (
            <StatusFilter
              statuses={status.data.statuses}
              defaults={status.data.saleStatusIds}
              value={listOf(search.statuses)}
              onChange={(ids) => set({ statuses: ids?.join(",") })}
            />
          )}
        </div>
      </FilterRow>
      <ReportStatus loading={q.isLoading} error={q.error} />
      {r && (
        <>
          <h2 className="h6 text-muted">Atividade da carteira em {formatDateBR(r.period.to)}</h2>
          <div className="row g-3 mb-3">
            {BUCKETS.map(([k, label]) => (
              <div className="col-12 col-md-4" key={k}>
                <KpiCard
                  label={label}
                  value={`${count(r.portfolio[k].customers)} cliente(s)`}
                  hint={`Receita total: ${money(r.portfolio[k].revenueCents)} · ${
                    k === "active"
                      ? `compraram nos últimos ${r.thresholds.activeDays} dias`
                      : k === "risk"
                        ? `última compra entre ${r.thresholds.activeDays + 1} e ${r.thresholds.riskDays} dias`
                        : `sem comprar há mais de ${r.thresholds.riskDays} dias`
                  }`}
                  testId={`portfolio-${k}`}
                />
              </div>
            ))}
          </div>
          <div className="mb-3">
            <ChartCard
              title={`Clientes novos × recorrentes (${periodLabel(r.period)})`}
              type="bar"
              stacked
              categories={r.months.map((m) => monthYear(m.month))}
              series={[
                { name: "Novos", data: r.months.map((m) => m.newCustomers) },
                { name: "Recorrentes", data: r.months.map((m) => m.returningCustomers) },
              ]}
              unit="count"
              filename="clientes-novos-recorrentes"
              testId="new-vs-returning"
            />
          </div>
          {r.lists && <PortfolioLists lists={r.lists} />}
        </>
      )}
      <Cohort
        month={search.month}
        channels={search.channels}
        statuses={search.statuses}
        onMonth={(m) => set({ month: m })}
      />
    </>
  );
}

function PortfolioLists({ lists }: { lists: Record<PortfolioBucket, PortfolioRowView[]> }): ReactElement {
  const [tab, setTab] = useState<PortfolioBucket>("risk");
  return (
    <section className="box card mb-3" aria-labelledby="pf-title">
      <div className="card-header">
        <h2 id="pf-title" className="h6 mb-0">
          Clientes por faixa (maiores compradores)
        </h2>
      </div>
      <ul className="nav nav-tabs px-3 pt-2" role="tablist" aria-label="Faixa da carteira">
        {BUCKETS.map(([k, label]) => (
          <li className="nav-item" key={k} role="presentation">
            <button
              type="button"
              role="tab"
              aria-selected={tab === k}
              className={`nav-link ${tab === k ? "active" : ""}`}
              onClick={() => setTab(k)}
            >
              {label} ({lists[k].length})
            </button>
          </li>
        ))}
      </ul>
      {lists[tab].length === 0 ? (
        <p className="text-muted p-3 mb-0">Nenhum cliente nesta faixa.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0" aria-label="Clientes da faixa">
            <thead>
              <tr>
                <th scope="col">Cliente</th>
                <th scope="col">Última compra</th>
                <th scope="col" className="text-end">
                  Dias sem comprar
                </th>
                <th scope="col" className="text-end">
                  Pedidos
                </th>
                <th scope="col" className="text-end">
                  Receita total
                </th>
              </tr>
            </thead>
            <tbody>
              {lists[tab].map((c) => (
                <tr key={c.contactId}>
                  <td>{c.name}</td>
                  <td>{formatDateBR(c.lastDay)}</td>
                  <td className="text-end">{count(c.recencyDays)}</td>
                  <td className="text-end">{count(c.orders)}</td>
                  <td className="text-end">{money(c.revenueCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Cohort por mês da primeira compra: retenção em mapa de calor (a intensidade é a retenção). */
function Cohort({
  month,
  channels,
  statuses,
  onMonth,
}: {
  month: string | undefined;
  channels: string | undefined;
  statuses: string | undefined;
  onMonth(m: string | undefined): void;
}): ReactElement {
  const { companyId, api } = useSession();
  const params = { month, channels, statuses };
  const q = useQuery({
    ...reportQueryOptions,
    queryKey: ["analytics", companyId, "cohort", params],
    queryFn: () => api<CohortResult>(`/analytics/cohort${reportQuery(params)}`),
  });
  const r = q.data;
  const width = r ? Math.max(0, ...r.cohorts.map((c) => c.cells.length)) : 0;
  return (
    <section className="box card mb-3" aria-labelledby="cohort-title" data-testid="cohort">
      <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
        <h2 id="cohort-title" className="h6 mb-0">
          Cohort de clientes (mês da primeira compra)
        </h2>
        <div className="d-flex align-items-center gap-2">
          <label className="small text-muted" htmlFor="cohort-month">
            Até o mês
          </label>
          <input
            id="cohort-month"
            type="month"
            className="form-control form-control-sm"
            style={{ width: "10rem" }}
            value={month ?? todayInSaoPaulo().slice(0, 7)}
            onChange={(e) => onMonth(e.target.value || undefined)}
          />
        </div>
      </div>
      <ReportStatus loading={q.isLoading} error={q.error} />
      {r && (
        <div className="table-responsive">
          <table className="table table-sm table-bordered text-center align-middle mb-0" aria-label="Cohort">
            <thead>
              <tr>
                <th scope="col" className="text-start">
                  Safra
                </th>
                <th scope="col">Clientes</th>
                {Array.from({ length: width }, (_, i) => (
                  <th scope="col" key={i}>
                    Mês {i}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {r.cohorts.map((c) => (
                <tr key={c.month}>
                  <th scope="row" className="text-start fw-normal">
                    {monthYear(c.month)}
                  </th>
                  <td>{count(c.size)}</td>
                  {Array.from({ length: width }, (_, i) => {
                    const cell = c.cells[i];
                    if (!cell) return <td key={i} className="bg-body-tertiary" />;
                    const alpha = c.size ? Math.max(0.08, cell.retentionPct4 / 1_000_000) : 0;
                    return (
                      <td
                        key={i}
                        title={`${count(cell.customers)} cliente(s)`}
                        style={{
                          backgroundColor: c.size
                            ? `rgba(var(--bs-primary-rgb), ${alpha.toFixed(2)})`
                            : undefined,
                          color: alpha > 0.55 ? "var(--bs-white)" : undefined,
                        }}
                      >
                        {c.size ? pct(cell.retentionPct4) : "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
